import { useState } from "react";
import { toast } from "sonner";
import { Link2, Share2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, PUBLIC_ORIGIN } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

export const shareUrl = (sid) => `${PUBLIC_ORIGIN}/api/s/${sid}`;

export const ShareToggle = ({ c }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [state, setState] = useState({ shared: !!c.shared, sid: c.share_id, busy: false });
  const [feat, setFeat] = useState(c.featured_status || "none");
  if (c.status !== "done" || user?.role === "demo") return null;
  const copy = async (sid) => {
    await navigator.clipboard.writeText(shareUrl(sid)).catch(() => {});
    toast.success(t("sh.copied"));
  };
  const toggle = async (enabled) => {
    setState((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post(`/clips/${c.id}/share`, { enabled });
      setState({ shared: data.shared, sid: data.share_id, busy: false });
      if (data.shared) copy(data.share_id);
    } catch (e) {
      toast.error(formatApiError(e));
      setState((s) => ({ ...s, busy: false }));
    }
  };
  const propose = async (enabled) => {
    try {
      const { data } = await api.post(`/clips/${c.id}/feature`, { enabled });
      setFeat(data.featured_status);
      if (enabled) toast.success(t("sh.feat_sent"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (<>
    <div className="mt-3 flex items-center gap-2 rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2" data-testid="clip-share-row">
      <Share2 className="w-3.5 h-3.5 text-[#3ad7ff]" />
      <span className="text-xs text-slate-300 whitespace-nowrap">{t("sh.share")}</span>
      {state.shared && c.share_views > 0 && <span className="text-[10px] font-mono text-slate-500 whitespace-nowrap hidden 2xl:inline" data-testid="clip-share-views">{c.share_views} {t("sh.views")}</span>}
      <div className="ml-auto flex items-center gap-2">
        {state.shared && state.sid && (
          <button onClick={() => copy(state.sid)} data-testid="clip-share-copy-btn" title={t("sh.copy")} className="text-[11px] text-[#3ad7ff] hover:text-white flex items-center gap-1 whitespace-nowrap">
            <Link2 className="w-3.5 h-3.5" /><span className="hidden xl:inline">{t("sh.copy")}</span>
          </button>
        )}
        <Switch checked={state.shared} disabled={state.busy} onCheckedChange={toggle} data-testid="clip-share-switch" />
      </div>
    </div>
    {state.shared && (
      <label className="mt-2 flex items-start gap-2 text-[11px] text-slate-400 cursor-pointer" title={t("sh.feat_consent")} data-testid="clip-feature-row">
        <input type="checkbox" checked={feat !== "none"} onChange={(e) => propose(e.target.checked)} data-testid="clip-feature-checkbox" className="mt-0.5 accent-[#a746ff]" />
        <span>{t("sh.feature")}{feat !== "none" && <span className="ml-1 font-mono text-[#c79bff]" data-testid="clip-feature-status">· {t(`stt.feat_${feat}`)}</span>}</span>
      </label>
    )}
  </>);
};
